import { type CardholderInfo } from '@dni-types/data-models/ecommerce-payment'
import { type UserPaymentTokenInfo } from '@hooks-api/types/dataModel/eCommercePayments'
import { Box } from '@pets-ui/Box/Box'
import { type CheckboxProps } from '@pets-ui/Checkbox/Checkbox'
import { HStack } from '@pets-ui/HStack/HStack'
import { type SelectOptionValue } from '@pets-ui/SelectOption/SelectOption'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import type * as stylex from '@stylexjs/stylex'
import { type ReactNode } from 'react'
import { TappayCreditCardFrameConfig } from '../constants/tappay'
import { CreditCardFields } from './CreditCardFields'
import { CreditCardInputField } from './CreditCardInputField'
export interface CreditCardFieldErrorMessage {
  creditCardNumber?: string
  cardholderName?: string
  cardholderEmail?: string
  cardholderPhoneNumber?: string
  /** For complete cardholder info saved by us */
  cardholderInfoComplete?: {
    cardholderName?: string
    cardholderEmail?: string
    cardholderPhone?: string
  }
}

export interface TappayCreditCardFieldsProps {
  /**
   * 使用 css 取代 js 控制是否顯示，這樣可以讓 SDK 不用每次都重新初始化，增加效能和體驗
   */
  isVisible: boolean
  errorMessage?: CreditCardFieldErrorMessage
  rememberPaymentCardTokenChecked?: CheckboxProps['checked']
  onRememberPaymentCardTokenChange?: CheckboxProps['onChange']
  canRememberPaymentCardToken?: boolean
  disabledChangeRememberPaymentCard?: boolean
  /**
   * 已記憶的信用卡
   */
  userPaymentTokenInfos?: UserPaymentTokenInfo[]
  /**
   * 選擇的信用卡
   */
  selectedUserPaymentTokenInfo?: SelectOptionValue
  onChangeSelectUserPaymentTokenInfo?: (newValue: SelectOptionValue) => void
  /**
   * 選擇卡號或新增卡號
   */
  onChangeCardSource?: (value: string) => void
  /**
   * 卡號來源
   */
  cardSource?: string | null
  /**
   * Styles for CreditCardInputField components
   */
  cardNumberStyle?: stylex.StyleXStyles
  expirationDateStyle?: stylex.StyleXStyles
  ccvStyle?: stylex.StyleXStyles
  nameStyle?: stylex.StyleXStyles
  emailStyle?: stylex.StyleXStyles
  phoneNumberStyle?: stylex.StyleXStyles
  /**
   * 讓月份/年份欄位填滿剩餘空間
   */
  expandExpirationDate?: boolean
  translateText?: Record<string, string>
  renderCheckoutCreditCardRewardContent?: () => ReactNode
  cardholderInfo?: CardholderInfo
  renderFieldsOfRememberedCard?: () => ReactNode
}

export const TappayCreditCardFields = (props: TappayCreditCardFieldsProps) => {
  return (
    <CreditCardFields {...props}>
      <VStack gap="8px">
        <VStack gap="4px">
          <Text color={colors.grey800}>{props.translateText?.creditCardNumber}</Text>
          <CreditCardInputField
            id={TappayCreditCardFrameConfig.CARD_NUMBER_FRAME}
            errorMessage={props.errorMessage?.creditCardNumber}
            style={props.cardNumberStyle}
          />
        </VStack>
        <HStack alignItems="center" gap="12px" flexWrap="nowrap">
          <VStack gap="4px" flex={props.expandExpirationDate ? 1 : undefined}>
            <Text color={colors.grey800}>{props.translateText?.creditCardExpiration}</Text>
            <CreditCardInputField
              id={TappayCreditCardFrameConfig.EXPIRATION_DATE_FRAME}
              style={props.expirationDateStyle}
            />
          </VStack>
          <VStack width={78}>
            <Text color={colors.grey800}>{props.translateText?.creditCardCode}</Text>
            <CreditCardInputField
              id={TappayCreditCardFrameConfig.CCV_FRAME}
              style={props.ccvStyle}
            />
          </VStack>
        </HStack>
        <VStack gap="4px">
          <Text color={colors.grey800}>{props.translateText?.cardholderName}</Text>
          <CreditCardInputField
            id={TappayCreditCardFrameConfig.CARDHOLDER_NAME_FRAME}
            style={props.nameStyle}
            errorMessage={props.errorMessage?.cardholderName}
          />
        </VStack>
        <VStack gap="4px">
          <Text color={colors.grey800}>{props.translateText?.cardholderEmail}</Text>
          <CreditCardInputField
            id={TappayCreditCardFrameConfig.CARDHOLDER_EMAIL_FRAME}
            style={props.emailStyle}
            errorMessage={props.errorMessage?.cardholderEmail}
          />
        </VStack>
        <VStack gap="4px">
          <Text color={colors.grey800}>{props.translateText?.cardholderPhone}</Text>
          <VStack gap="4px">
            <HStack gap="8px" flexWrap="nowrap">
              <Box width="60px">
                <CreditCardInputField
                  id={TappayCreditCardFrameConfig.CARDHOLDER_PHONE_COUNTRY_FRAME}
                  style={props.phoneNumberStyle}
                />
              </Box>
              <Box flexGrow={1}>
                <CreditCardInputField
                  id={TappayCreditCardFrameConfig.CARDHOLDER_PHONE_NUMBER_FRAME}
                  style={props.phoneNumberStyle}
                />
              </Box>
            </HStack>
            <Text color={colors.red600}>{props.errorMessage?.cardholderPhoneNumber}</Text>
          </VStack>
        </VStack>
        <Text variant="caption" color={colors.grey600}>
          {props?.translateText?.cardholderEmailOrPhoneRequired}
        </Text>
      </VStack>
    </CreditCardFields>
  )
}
